import { createPersistedStore, storage } from '@connect-x-app/hybrid-utils';
import { ConnectivityStatus, FlightRoomUser, UserUpdateEvent } from '@connect-x-app/shared-domain-models';
import { createSerializableStorage } from '@connect-x-app/shared-utils';

type UserMap = Map<string, FlightRoomUser>;

type UserState = {
  updateKnownUser: (user: UserUpdateEvent) => void;
  updateUserConnectivityStatus: (externalUserId: string, connectivityStatus: ConnectivityStatus) => void;
  updateUsers: (users: FlightRoomUser[]) => void;
  userMap: UserMap;
};

export const useUsersStore = createPersistedStore<UserState>(
  (set, get) => ({
    updateKnownUser: user => {
      const existingUser = get().userMap.get(user.externalId);
      // `user.update` is broadcast for every user in the account, not only the ones we share a room
      // with. Ignore unknown users, otherwise the persisted map grows unbounded.
      if (!existingUser) {
        return;
      }

      const { username, ...updatedFields } = user;

      set(state => ({
        userMap: new Map(state.userMap).set(user.externalId, {
          ...existingUser,
          ...updatedFields,
          id: existingUser.id,
          roles: existingUser.roles,
          userName: username,
        }),
      }));
    },
    updateUserConnectivityStatus: (externalUserId, connectivityStatus) => {
      const userMap = get().userMap;
      const user = userMap.get(externalUserId);
      if (user) {
        set(state => ({
          userMap: new Map(state.userMap).set(externalUserId, {
            ...user,
            connectivityStatus,
          }),
        }));
      }
    },
    updateUsers: flightRoomUsers =>
      set(state => {
        const newUserMap = new Map(state.userMap);
        flightRoomUsers.forEach(flightRoomUser => {
          newUserMap.set(flightRoomUser.externalId, flightRoomUser);
        });
        return { userMap: newUserMap };
      }),
    userMap: new Map<string, FlightRoomUser>(),
  }),
  {
    name: 'users-store',
    storage: createSerializableStorage<UserState>(storage),
  },
);

export const getUserByExternalId = (externalId: string): FlightRoomUser | undefined =>
  useUsersStore.getState().userMap.get(externalId);
